import { useEffect, useState, useCallback } from "react";
import { Search, UserPlus, Ban, RotateCcw, Trash2, Users, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FeesPanel, { FeeStatusBadge } from "@/components/FeesPanel";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "usr.role_admin", music_leader: "usr.role_music_leader", treasurer: "usr.role_treasurer", musician: "usr.role_musician", member: "usr.role_member" };
const ROLE_CLS = {
  admin: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  music_leader: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  treasurer: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  musician: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  member: "bg-sky-500/15 text-sky-400 border-sky-500/30",
};

export default function UtilisateursPage() {
  const { user, group, t } = useAuth();
  const isAdmin = user?.role === "admin";
  const isSchool = group?.kind === "school";
  // Espace enseignement : statuts « Professeur » (admin) et « Élève » (musicien)
  const roleOptions = isSchool ? { admin: "usr.role_prof", musician: "usr.role_student" } : ROLE_LABELS;
  const canReadFees = ["admin", "music_leader", "treasurer"].includes(user?.role);
  const [feeMap, setFeeMap] = useState({});
  useEffect(() => {
    if (canReadFees) api.get("/fees").then((r) => {
      const m = {};
      (r.data.rows || []).forEach((row) => { m[row.member_id] = row.status; });
      setFeeMap(m);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [members, setMembers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", instrument: "", role: "musician" });
  const [invitedTo, setInvitedTo] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [delMember, setDelMember] = useState(null);

  const load = useCallback(() => {
    api.get("/members?include_inactive=true").then((r) => setMembers(r.data)).catch((e) => toast.error(e.friendly));
    if (isAdmin) api.get("/invitations").then((r) => setInvitations(r.data)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const filtered = members
    .filter((m) => showInactive || m.active)
    .filter((m) => {
      const q = search.toLowerCase();
      return !q || `${m.prenom} ${m.nom} ${m.login_id}`.toLowerCase().includes(q);
    });

  const create = async () => {
    try {
      await api.post("/invitations", { ...form, origin_url: window.location.origin });
      setInvitedTo(form.email);
      setForm({ prenom: "", nom: "", email: "", instrument: "", role: "musician" });
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const resendInvitation = async (inv) => {
    try {
      await api.post(`/invitations/${inv.id}/resend`, { origin_url: window.location.origin });
      toast.success(t("usr.invitation_resent"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const cancelInvitation = async (inv) => {
    try {
      await api.delete(`/invitations/${inv.id}`);
      toast.success(t("usr.invitation_cancelled"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/members/${m.id}`, { role });
      toast.success(t("usr.role_updated"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const changeInstrument = async (m, instrument) => {
    if ((m.instrument || "") === instrument) return;
    try {
      await api.put(`/members/${m.id}`, { instrument });
      toast.success(t("usr.instrument_updated"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const toggleActive = async (m) => {
    try {
      await api.post(`/members/${m.id}/${m.active ? "deactivate" : "reactivate"}`);
      toast.success(m.active ? t("usr.deactivated") : t("usr.reactivated"));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  const resetPassword = async (m) => {
    try {
      await api.post(`/members/${m.id}/reset-password`);
      toast.success(t("usr.reset_done", { name: m.prenom }));
      load();
    } catch (e) { toast.error(e.friendly); }
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6 brand" /> {t("usr.title")}</h2>
        {isAdmin && (
          <Button className="brand-bg text-white" onClick={() => { setCreateOpen(true); setInvitedTo(null); }} data-testid="new-user-button">
            <UserPlus className="h-4 w-4 mr-1" /> {t("usr.new")}
          </Button>
        )}
      </div>

      <Tabs defaultValue="membres">
      {canReadFees && (
        <TabsList className="card-bg border border-app">
          <TabsTrigger value="membres" data-testid="tab-membres">{t("usr.title")}</TabsTrigger>
          <TabsTrigger value="cotisations" data-testid="tab-cotisations">{t("fees.tab")}</TabsTrigger>
        </TabsList>
      )}
      <TabsContent value="membres" className="space-y-4 mt-4">
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-52 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 t-secondary" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("usr.search_placeholder")} className="pl-9 bg-transparent border-app" data-testid="users-search-input" />
        </div>
        <label className="flex items-center gap-2 text-sm t-secondary cursor-pointer">
          <input type="checkbox" className="accent-emerald-500" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="show-inactive-checkbox" />
          {t("usr.show_inactive")}
        </label>
      </div>

      <div className="app-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-app text-left">
              <th className="p-3">{t("usr.col_name")}</th>
              <th className="p-3 hidden sm:table-cell">ID</th>
              <th className="p-3">{t("usr.col_role")}</th>
              <th className="p-3">{t("usr.col_instrument")}</th>
              {canReadFees && <th className="p-3">{t("fees.col")}</th>}
              {isAdmin && <th className="p-3 text-right">{t("usr.col_actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className={`border-b border-app/30 ${!m.active ? "opacity-50" : ""}`} data-testid={`user-row-${m.id}`}>
                <td className="p-3 font-medium">
                  {m.prenom} {m.nom}
                  {m.id === user.id && <span className="text-xs brand ml-2">{t("usr.you")}</span>}
                  {m.status === "PENDING" && <span className="text-xs accent ml-2">{t("usr.pending")}</span>}
                  {(m.status === "DISABLED" || (!m.status && !m.active)) && <span className="text-xs text-rose-400 ml-2">{t("usr.disabled")}</span>}
                </td>
                <td className="p-3 font-mono text-xs t-secondary hidden sm:table-cell">{m.email || m.login_id}</td>
                <td className="p-3">
                  {isAdmin && m.id !== user.id ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                      <SelectTrigger className={`h-8 w-32 sm:w-48 text-xs border ${ROLE_CLS[m.role] || ROLE_CLS.member}`} data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(roleOptions).map(([k, l]) => <SelectItem key={k} value={k}>{t(l)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${ROLE_CLS[m.role] || ROLE_CLS.member}`}>{t(roleOptions[m.role] || ROLE_LABELS[m.role] || m.role)}</span>
                  )}
                </td>
                <td className="p-3 t-secondary">
                  {isAdmin ? (
                    (group?.instruments || []).length > 0 ? (
                      <Select value={(group.instruments.includes(m.instrument) ? m.instrument : "") || undefined} onValueChange={(v) => changeInstrument(m, v)}>
                        <SelectTrigger className="h-8 w-32 sm:w-44 text-xs border border-app bg-transparent" data-testid={`instrument-select-${m.id}`}>
                          <SelectValue placeholder={m.instrument || t("usr.choose")} />
                        </SelectTrigger>
                        <SelectContent>{group.instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input
                        defaultValue={m.instrument || ""}
                        className="h-8 w-32 sm:w-44 text-xs bg-transparent border-app"
                        data-testid={`instrument-input-${m.id}`}
                        onBlur={(e) => changeInstrument(m, e.target.value.trim())}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      />
                    )
                  ) : (
                    m.instrument || "—"
                  )}
                </td>
                {canReadFees && (
                  <td className="p-3">{feeMap[m.id] ? <FeeStatusBadge status={feeMap[m.id]} /> : <span className="t-secondary">—</span>}</td>
                )}
                {isAdmin && (
                  <td className="p-3 text-right">
                    {m.id !== user.id && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => resetPassword(m)} className="p-2 t-secondary hover:text-sky-400" title={t("usr.reset_title")} data-testid={`reset-password-${m.id}`}>
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button onClick={() => toggleActive(m)} className="p-2 t-secondary hover:text-amber-400" title={m.active ? t("usr.deactivate_title") : t("usr.reactivate_title")} data-testid={`toggle-active-${m.id}`}>
                          {m.active ? <Ban className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                        </button>
                        <button onClick={() => setDelMember(m)} className="p-2 t-secondary hover:text-rose-400" title={t("usr.delete_title_attr")} data-testid={`delete-user-${m.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm t-secondary p-6 text-center">{t("usr.none_found")}</p>}
      </div>

      {isAdmin && invitations.length > 0 && (
        <div className="app-card p-4 space-y-2" data-testid="pending-invitations">
          <p className="t-secondary text-xs uppercase tracking-wider">{t("usr.pending_invitations")}</p>
          {invitations.map((inv) => (
            <div key={inv.id} className="flex items-center gap-3 py-1.5 border-b border-app/30 last:border-0 flex-wrap" data-testid={`invitation-row-${inv.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{inv.prenom} {inv.nom} <span className="t-secondary font-normal">· {inv.email}</span></p>
                <p className="text-xs t-secondary flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-amber-400 shrink-0" />
                  {t("usr.invitation_pending")} · {t(roleOptions[inv.role] || ROLE_LABELS[inv.role] || inv.role)}{inv.instrument ? ` · ${inv.instrument}` : ""}
                </p>
              </div>
              <Button size="sm" variant="outline" className="border-app" onClick={() => resendInvitation(inv)} disabled={inv.expired} data-testid={`invitation-resend-${inv.id}`}>
                {t("usr.resend_invitation")}
              </Button>
              <Button size="sm" variant="outline" className="border-app text-rose-400" onClick={() => cancelInvitation(inv)} data-testid={`invitation-cancel-${inv.id}`}>
                {t("usr.cancel_invitation")}
              </Button>
            </div>
          ))}
        </div>
      )}

      </TabsContent>
      {canReadFees && (
        <TabsContent value="cotisations" className="mt-4">
          <FeesPanel />
        </TabsContent>
      )}
      </Tabs>

      {/* Création */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setInvitedTo(null); }}>
        <DialogContent className="card-bg border-app" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("usr.create_title")}</DialogTitle>
            <DialogDescription className="t-secondary">{t("usr.create_desc")}</DialogDescription>
          </DialogHeader>
          {invitedTo ? (
            <div className="text-center py-4 space-y-3" data-testid="invitation-sent">
              <p className="text-sm t-secondary">{t("usr.invitation_sent_msg")}</p>
              <p className="font-mono text-sm brand font-bold">{invitedTo}</p>
              <Button className="brand-bg text-white" onClick={() => { setCreateOpen(false); setInvitedTo(null); }} data-testid="invitation-sent-close">{t("common.close")}</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")} *</Label>
                  <Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="new-user-prenom-input" />
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                  <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="new-user-nom-input" />
                </div>
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("auth.email_label")} *</Label>
                <Input type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("auth.email_ph")} className="mt-1 bg-transparent border-app" data-testid="new-user-email-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("instrument_label")}</Label>
                {(group?.instruments || []).length > 0 ? (
                  <Select value={form.instrument} onValueChange={(v) => setForm({ ...form, instrument: v })}>
                    <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="new-user-instrument-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                    <SelectContent>{group.instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="new-user-instrument-input" />
                )}
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("usr.col_role")} *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleOptions).map(([k, l]) => <SelectItem key={k} value={k}>{t(l)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full brand-bg text-white" onClick={create} disabled={!form.prenom.trim() || !form.email.trim()} data-testid="create-user-submit">
                {t("usr.send_invitation")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!delMember}
        title={t("usr.delete_title")}
        description={t("usr.delete_desc", { name: `${delMember?.prenom || ""} ${delMember?.nom || ""}`.trim() })}
        onConfirm={async () => {
          await api.delete(`/members/${delMember.id}`).catch((e) => toast.error(e.friendly));
          setDelMember(null);
          load();
        }}
        onClose={() => setDelMember(null)}
        testid="delete-user-confirm-dialog"
      />
    </div>
  );
}
